import { DisplayResponse } from "@/lib/depict-client/types/DisplayResponse"
import { Variant } from "@/lib/depict-client/types/Variant"
import { MinimizedCentraProduct } from "@/types/centra/checkout-api/products/post-products"

export function transformDepictDisplayResponse<T extends DisplayResponse>(
    res: T | undefined | null,
    key: keyof Variant
): Array<{ key: string; items: MinimizedCentraProduct[] }> {
    const items =
        res?.displays?.flatMap((display: any) => {
            // Grab the first variant based on index.
            const primaryVariant = display.variant_displays?.splice(
                display.variant_index,
                1
            )
            display.variant_displays.unshift(primaryVariant[0])
            return (
                display.variant_displays?.map((variant: Variant) => ({
                    ...variant,
                    search_result_id: display.search_result_id,
                    recommendation_id: display.recommendation_id,
                    product_listing_result_id: display.product_listing_result_id
                })) || []
            )
        }) || []
    return transformDepictResponse(items, key)
}

export function transformDepictResponse<T extends Variant>(
    items: T[],
    key: keyof T
): Array<{ key: string; items: MinimizedCentraProduct[] }> {
    const colors: Record<string, number> = {}
    const groups = items.reduce((acc, item) => {
        const groupKey = String(item[key])
        const colorKey = String(item[key] + "_" + item.specific_color)
        if (!acc.has(groupKey)) {
            acc.set(groupKey, [])
        }
        if (!colors[colorKey]) {
            acc.get(groupKey)!.push({
                search_result_id: item.search_result_id,
                recommendation_id: item.recommendation_id,
                product_listing_result_id: item.product_listing_result_id,
                uri: item.uri,
                name: item.title,
                available: item.in_stock,
                price: item.sale_price,
                priceBeforeDiscount: item.original_price,
                priceAsNumber: item.price_as_number,
                color_swatch: {
                    color_name: item.color_name,
                    hex: item.color_hex,
                    filter_color: item.filter_color
                },
                mediaObjects: {
                    big: item.image_urls
                        .slice(0, 2)
                        .map(source =>
                            source.includes("-big")
                                ? source
                                : source
                                      .replace("-full", "-big")
                                      .replace(/(\.jpg)$/, "-big$1")
                        ),
                    standard: item.image_urls
                        .slice(0, 2)
                        .map(source => source.replace("-full", "")),
                    small: item.image_urls
                        .slice(0, 2)
                        .map(source =>
                            source.includes("-small")
                                ? source
                                : source
                                      .replace("-full", "-small")
                                      .replace(/(\.jpg)$/, "-small$1")
                        )
                },
                showAsOnSale: item.show_as_sale,
                showAsNew: item.show_as_new,
                badge: item.badge,
                centraProduct: item.product_id,
                items: `${item.main_product_id}-${item.product_id}`,
                lens: {
                    lens_name: item.lens_name,
                    hex: item.lens_hex
                },
                variant_sort_order_sort_order: item.variant_sorder_order,
                fit: item.fit,
                itemTableX: items.reduce((a: string[], i) => {
                    if (i.item_group_id === groupKey && !a.includes(i.size)) {
                        a.push(i.size)
                    }
                    return a
                }, []),
                product_benefits_list: item.product_benefits_list
            })
            colors[colorKey] = 1
        }
        return acc
    }, new Map<string, MinimizedCentraProduct[]>())
    return Array.from(groups.entries()).map(([groupKey, items]) => ({
        key: groupKey,
        items: items
    }))
}
